<template>
  <div>
    <section :class="$style.view">
      <h2 :class="[$style.view__header, $style.header]">
        {{ titles }}への同意のお願い
      </h2>
      <div ref="scrollAreaRef" :class="[$style.view__content, $style.content]">
        <div ref="contentsRef">
          <AppMarkdown
            :class="[$style.content__md, $style.md]"
            :body="term.body"
            :key="term.id"
            v-for="term in _terms"
          />
        </div>
      </div>
      <form
        :class="[$style.view__form, $style.form]"
        @submit.prevent="onSubmit"
      >
        <div>
          <label :class="$style.check" @click="handleClickCheck"
            ><DolphinCheckbox
              :class="$style.check__checkbox"
              v-model="check"
              :disabled="disableCheckbox"
            /><span :class="$style.check__text"
              >上記に同意してサービスを利用します</span
            ></label
          >
        </div>
        <FlatButton
          size="medium"
          variant="plain"
          :class="[$style.form__submit, $style.button]"
          :disabled="!check"
          type="submit"
        >
          送信
        </FlatButton>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import AppMarkdown from '@/components/atoms/AppMarkdown.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useElementSize, useScroll } from '@vueuse/core'
import { useAlert } from '@/libs/alertPortal'
import type { AgreementViewRequiredAgreementTermsFragment } from './AgreementView.generated'

const emit = defineEmits<{
  (
    event: 'agree',
    value: AgreementViewRequiredAgreementTermsFragment['requiredAgreementTerms']
  ): void
}>()
const props = defineProps<{
  terms?: AgreementViewRequiredAgreementTermsFragment
}>()
const _terms = computed(() => props.terms?.requiredAgreementTerms ?? [])

const titles = computed(() =>
  _terms.value?.map(({ title }) => title).join('、')
)

const scrollAreaRef = ref<HTMLElement | null>(null)
const contentsRef = ref<HTMLElement | null>(null)

const { arrivedState } = useScroll(scrollAreaRef, { offset: { bottom: 100 } })

const bottomReached = ref(false)
const stopReadWatcher = watch(
  () => arrivedState.bottom,
  bottomArrived => {
    if (!bottomArrived) {
      return
    }
    bottomReached.value = true
    stopReadWatcher()
  }
)

const scrollAreaSize = useElementSize(scrollAreaRef)
const contentsSize = useElementSize(contentsRef)
const unscrollable = computed(
  () => scrollAreaSize.height.value >= contentsSize.height.value
)
const disableCheckbox = computed(
  () => !unscrollable.value && !bottomReached.value
)

const check = ref(false)
const onSubmit = () => {
  if (!check.value) {
    return
  }
  emit('agree', _terms.value ?? [])
}

const { warning } = useAlert()
const handleClickCheck = () => {
  if (!disableCheckbox.value) {
    return
  }
  warning('最後までご覧になった後に同意をお願いいたします')
}
</script>

<style lang="scss" module>
.view {
  display: flex;
  height: 100%;
  flex-direction: column;

  &__form {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.content {
  overflow: scroll;

  &:nth-child(n + 2) {
    margin-top: 2em;
  }

  &__md {
    $gap: 4em;

    &:nth-child(n + 2) {
      padding-top: $gap;
      border-top: 1px solid $color-border-low-emphasis;
      margin-top: $gap;
    }
  }
}

.form {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__submit {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }
}

.check {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &__text {
    :disabled + & {
      opacity: 0.6;
    }
  }
}

.button {
  width: 100%;
  max-width: 240px;
}

.md {
  h1,
  h2,
  h3 {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  p {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  /* stylelint-disable no-descending-specificity */
  ol {
    padding-left: 2em;

    li {
      list-style: decimal;
    }

    ul,
    ol {
      margin-top: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 2em;
    }
  }

  ul {
    padding-left: 1.5em;

    li {
      list-style: disc;

      &:nth-child(n + 2) {
        margin-top: 0.5em;
      }

      li {
        list-style: circle;
      }
    }

    ul,
    ol {
      margin-top: 0.75em;
    }

    &:nth-child(n + 2) {
      margin-top: 2em;
    }

    ul:nth-child(n + 2) {
      margin-top: 0.5em;
    }
  }

  /* stylelint-enable no-descending-specificity */

  hr {
    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }
  }

  img {
    max-width: 100%;
  }

  table {
    border-spacing: 0;
    border-collapse: collapse;
    display: block;

    &:nth-child(n + 2) {
      margin-top: 1.75em;
    }

    th,
    td {
      padding: 6px 13px;
      border: 1px solid black;
    }

    tr {
      border: 1px solid black;
    }
  }
}
</style>
